import { selectEntityDisplayName } from '@opengov/app-core/entity';
import { transport } from '@opengov/app-core/transport';
import { createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectUseQueryService } from '@opengov/ogql-client/selectors';
import { IOgqlJobResult } from '@opengov/ogql-types';
import { EQueryType } from '@opengov/reporting-types';
import isBlank from '@sedan-utils/is-blank';
import { Buffer } from 'buffer';
import saveAs from 'file-saver'; // should we use this?
import { produce } from 'immer';
import sanitize from 'sanitize-filename';
import { composeEffects } from '../../../module';
import { EPageOrientation, IReportPdfExport } from '../../../src/components/ExportReportPdfModal';
import { MuiToastTypes } from '../../common/helpers/utils';
import { reportLocaleText } from '../../common/locale';
import { selectCurrentReport } from '../../common/report/selectors/reportSelectors';
import { reportActionCreators } from '../../common/report/state';
import { runV0Query } from '../../common/reportConfiguration/effects/queries/runQueries';
import {
  selectCurrentReportConfiguration,
  selectDetailTableGroupByColumns,
  selectDetailTableShowTotals,
  selectDimensionalSummaryBlockExpandedStates,
  selectIsInStandardReport
} from '../../common/reportConfiguration/selectors';
import {
  selectCurrentLens,
  selectCurrentTemplate,
  selectCurrentTemplateId
} from '../../common/reportTemplate/standardTemplateSelectors';
import {
  buildDetailTableQueryParams,
  jobResultToAggridRowData
} from '../ReportDataTableAgGrid/selectors/selectReportDataTableAgGrid';
import { IDimensionalAgGridRowItem, reportDimensionalEffects } from '../effects/reportDimensionalEffects';
import { selectDimensionalGroupData, selectReportConfigWithMetadata } from '../selectors/selectReportExplorerState';
import { reportExplorerActionCreators } from '../state/reportExplorerState';

export const PdfExportRowLimit = 10 * 1000;

const createPdfBlob = (pdfBuffer: unknown): Blob =>
  new Blob([Buffer.from(pdfBuffer as string, 'utf-8')], { type: 'application/pdf' });

const createPdfUrlString = (pdfBlob: Blob): string => URL.createObjectURL(pdfBlob);

export const downloadPdfToBrowser = (pdfBlob: Blob, reportTitle = 'opengovReport'): void =>
  saveAs(pdfBlob, `${reportTitle}.pdf`);

// This needs to be in somewhere else, originally from stories-shared library
export function b64EncodeUnicode(str) {
  // Check if the method is called from a node environment
  if (process.versions && process.versions.node) {
    return Buffer.from(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
        return String.fromCharCode(Number(`0x${p1}`));
      }),
      'ascii'
    ).toString('base64');
  }
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode(Number(`0x${p1}`));
    })
  );
}

const createPdfRequest = async (params: IReportPdfExport): Promise<Blob> => {
  try {
    const headerHtmlEncoded = params.headerHtml ? b64EncodeUnicode(params.headerHtml) : undefined;
    const continuationHeaderHtmlEncoded = params.continuationHeaderHtml
      ? b64EncodeUnicode(params.continuationHeaderHtml)
      : undefined;

    const response = await transport
      .url('/api/content_export_service/v2/pdf')
      .json({
        contentHtmlEncoded: b64EncodeUnicode(params.contentHtml),
        footerHtmlEncoded: b64EncodeUnicode(params.footerHtml),
        headerHtmlEncoded,
        continuationHeaderHtmlEncoded,
        isLandscape: params.pageOrientation === EPageOrientation.landscape
      })
      .post()
      .json();

    return createPdfBlob(response.pdf);
  } catch (error) {
    console.error(error);
    throw new Error('function createPdfRequest failed');
  }
};

export const reportPdfEffects = composeEffects('reportPdfEffects', (compose) => ({
  fetchAccountSummaryTableData: compose.statefulPayloadlessEffect(
    createStructuredSelector({
      groupData: selectDimensionalGroupData,
      reportConfiguration: selectCurrentReportConfiguration,
      blockExpandedStates: selectDimensionalSummaryBlockExpandedStates
    }),
    async ({ groupData, reportConfiguration, blockExpandedStates }) => {
      reportExplorerActionCreators.setFetchingPdfExportData(true);

      let data = [];

      for (const group of groupData) {
        data.push(group);

        const index = groupData.indexOf(group);
        if (blockExpandedStates.every((state) => state.controlBlockIndex !== index)) {
          // not expanded .. no need for the children
          continue;
        }

        const childData = await reportDimensionalEffects.getDimensionalChildData({
          reportConfiguration,
          index,
          limit: 100,
          offset: 0
        });

        const patchedData = (childData as unknown as IDimensionalAgGridRowItem).map((child) => {
          const name = child.name;
          (child.name as string[]).unshift(group.name[0]);
          return { ...child, name };
        });

        data = data.concat(patchedData);
      }

      if (data.length > PdfExportRowLimit) {
        reportExplorerActionCreators.setPdfExportTooMuchData(true);
      } else {
        reportExplorerActionCreators.setPdfExportData({
          accountSummaryTableData: data,
          detailTableData: [],
          detailTableTotalsData: []
        });
      }

      reportExplorerActionCreators.setFetchingPdfExportData(false);
    }
  ),

  fetchDetailTableExportPdfData: compose.statefulEffect(
    createStructuredSelector({
      reportMsgs: reportLocaleText,
      reportConfiguration: selectReportConfigWithMetadata,
      lens: selectCurrentLens,
      useQueryService: selectUseQueryService,
      isStandardTemplate: selectIsInStandardReport,
      templateId: selectCurrentTemplateId,
      showTotals: selectDetailTableShowTotals,
      groupBy: selectDetailTableGroupByColumns
    }),
    async (
      { reportMsgs, reportConfiguration, useQueryService, isStandardTemplate, templateId, lens, showTotals, groupBy },
      e: string[]
    ) => {
      const groupByColumn = reportConfiguration.detailTableQuery.groupByColumns[0];
      reportExplorerActionCreators.setFetchingPdfExportData(true);

      const patchedReportConfiguration = produce(reportConfiguration, (draft) => {
        draft.detailTableQuery.paginationLimit = 10000;
      });

      const data = await runV0Query({
        limit: PdfExportRowLimit,
        lensId: lens && lens.id,
        reportId: reportConfiguration.reportId,
        queryType: EQueryType.detailTableQuery,
        useLargeScaleQueryLimit: true,
        templateId,
        body: isStandardTemplate ? { reportConfiguration: patchedReportConfiguration } : patchedReportConfiguration,
        useQueryService,
        isStandardTemplate
      });

      const detailTableData = jobResultToAggridRowData(data.jobResult).map((row) => {
        if (row[groupByColumn] == null) {
          row[groupByColumn] = reportMsgs.reportExplorer.emptyValues;
        }
        return row;
      });

      let totalsData: IOgqlJobResult = null;
      if (showTotals) {
        totalsData = await runV0Query({
          lensId: lens && lens.id,
          templateId,
          reportId: reportConfiguration.reportId,
          queryType: EQueryType.detailTableSumQuery,
          body: isStandardTemplate ? { reportConfiguration } : reportConfiguration,
          useQueryService,
          isStandardTemplate
        });
      }

      const detailTableTotalsData = totalsData && jobResultToAggridRowData(totalsData.jobResult);

      if (!isBlank(groupBy) && e?.length) {
        let limitAvailable = 10000;
        for (const groupKey of e) {
          if (limitAvailable <= 0) {
            reportExplorerActionCreators.setFetchingPdfExportData(false);
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.FAILURE,
              headerText: reportMsgs.query.failure.headerText,
              bodyText: reportMsgs.query.failure.bodyText,
              dataTest: 'toast-load-report-failure'
            });
            return;
          }

          const queryParams = buildDetailTableQueryParams({
            groupKeys: [groupKey],
            reportConfiguration,
            isStandardTemplate,
            childRequest: true,
            limit: 10000,
            offset: 0,
            emptyValuesText: reportMsgs.reportExplorer.emptyValues
          });

          const childData = await runV0Query({
            ...queryParams,
            reportId: reportConfiguration.reportId,
            body: isStandardTemplate
              ? { reportConfiguration: queryParams.reportConfiguration }
              : queryParams.reportConfiguration,
            lensId: isStandardTemplate ? lens && lens.id : ''
          });

          const childTableData = jobResultToAggridRowData(childData.jobResult);
          childTableData.forEach((row) => {
            row[groupByColumn] = '';
            return row;
          });

          const indexOfParentRow = detailTableData.findIndex((row) => row[groupByColumn] === groupKey);
          detailTableData.splice(indexOfParentRow + 1, 0, ...childTableData);

          limitAvailable -= childTableData.length;
        }
      }

      reportExplorerActionCreators.setPdfExportData({
        detailTableData,
        detailTableTotalsData,
        accountSummaryTableData: []
      });
      reportExplorerActionCreators.setFetchingPdfExportData(false);
    }
  ),
  exportPdf: compose.statefulEffect(
    createStructuredSelector({
      localeText: reportLocaleText,
      currentReport: selectCurrentReport,
      currentTemplate: selectCurrentTemplate,
      isStandardTemplate: selectIsInStandardReport,
      entityName: selectEntityDisplayName
    }),
    async (args, e: IReportPdfExport): Promise<string> => {
      const { localeText, currentReport, entityName, currentTemplate, isStandardTemplate } = args;
      const { contentHtml, footerHtml, headerHtml, continuationHeaderHtml, pageOrientation } = e;
      const title = isStandardTemplate ? currentTemplate.standardReportName : currentReport.title;

      const { exportPdfModal } = localeText;

      reportActionCreators.toggleMuiToast({
        headerText: exportPdfModal.infoToastTitle,
        bodyText: exportPdfModal.infoToastBody,
        type: 'INFO',
        showLoader: true
      });

      try {
        const pdfBlob = await createPdfRequest({
          contentHtml,
          pageOrientation,
          footerHtml,
          headerHtml,
          continuationHeaderHtml
        });

        downloadPdfToBrowser(pdfBlob, sanitize(`${entityName}_${title}`));

        reportActionCreators.toggleMuiToast({
          headerText: exportPdfModal.successToastTitle,
          bodyText: exportPdfModal.successToastBody,
          type: 'SUCCESS'
        });
        return createPdfUrlString(pdfBlob);
      } catch (error) {
        reportActionCreators.toggleMuiToast({
          headerText: exportPdfModal.failureToastTitle,
          bodyText: exportPdfModal.failureToastBody,
          type: 'FAILURE'
        });
        throw error;
      }
    }
  ),
  generatePdfPreview: compose.effect(async (e: IReportPdfExport): Promise<string> => {
    const { contentHtml, pageOrientation, footerHtml, headerHtml, continuationHeaderHtml } = e;

    try {
      const pdfBlob = await createPdfRequest({
        contentHtml,
        pageOrientation,
        footerHtml,
        headerHtml,
        continuationHeaderHtml
      });
      return createPdfUrlString(pdfBlob);
    } catch (error) {
      console.error(error);
      throw error;
    }
  })
}));
